/**
 * Sector Legal Pages — Privacy Policy & Terms of Service
 * Each sub-species gets its own branded legal pages
 * Content follows Australian Privacy Act, GDPR, and sector-specific regulations
 */

import { SectorMarketingConfig } from './SectorMarketingData';

interface SectorLegalPageProps {
  config: SectorMarketingConfig;
  onBack: () => void;
  onNavigate: (view: string) => void;
}

// ═══════════════════════════════════════════════════════════════════════
// SECTOR PRIVACY POLICY
// ═══════════════════════════════════════════════════════════════════════
export const SectorPrivacyPolicy: React.FC<SectorLegalPageProps> = ({
  config,
  onBack,
  onNavigate,
}) => {
  const year = new Date().getFullYear();
  const sectorDisclaimer: Record<string, string> = {
    medical:
      'In accordance with HIPAA (USA), TGA (Australia), and the Health Records Act 2001 (Vic), we implement additional safeguards for Protected Health Information (PHI) and health records.',
    finance:
      'In accordance with APRA CPS 234, SOX, MiFID II, and PCI DSS, financial data is subject to enhanced encryption, access controls, and audit logging.',
    legal:
      'Legal professional privilege and solicitor-client confidentiality are maintained through end-to-end encryption and access controls. No legal communications are used for AI model training.',
    defence:
      'Defence-related data is handled under DISP (Defence Industry Security Program) and ITAR controls. Air-gapped processing is available for classified information.',
    climate:
      'Environmental data is processed in accordance with CDP (Carbon Disclosure Project) standards and TCFD recommendations.',
    energy:
      'Energy infrastructure data is handled under NERC CIP and AEMO security requirements. Critical infrastructure protections apply.',
    maritime:
      'Maritime data is processed in accordance with IMO guidelines and ISPS Code requirements. AIS data handling follows international maritime regulations.',
    space:
      'Space-related data may be subject to ITAR export controls. Orbital data is handled in accordance with US Space Command and ESA data sharing agreements.',
    agriculture:
      'Agricultural data is processed in accordance with FAO data standards. Farm-level data is anonymised unless explicit consent is provided.',
    education:
      'Student data is protected under FERPA (USA), GDPR Article 8 (EU), and the Privacy Act 1988 (Australia). Parental consent protocols are enforced for under-18 users.',
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} py-16`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={onBack}
            className="text-white/70 hover:text-white text-sm mb-6 flex items-center gap-1 transition-colors"
          >
            &larr; Back to {config.brandName}
          </button>
          <div className="flex items-center gap-3 mb-4">
            <img
              src={config.logoUrl}
              alt={config.brandName}
              draggable="false"
              className="h-12 w-12 rounded-xl object-cover select-none pointer-events-none shadow-lg"
            />
            <div>
              <h1 className="text-3xl font-bold">Privacy Policy</h1>
              <p className="text-white/70 text-sm">{config.companyStructure.entity}</p>
            </div>
          </div>
          <p className="text-white/60 text-sm">
            Last updated: 1 January {year} &bull; Effective: 1 January {year}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-invert prose-sm max-w-none space-y-8">
          {/* 1. Introduction */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>1. Introduction</h2>
            <p className="text-slate-300 leading-relaxed">
              {config.companyStructure.entity} (&ldquo;{config.brandName}&rdquo;, &ldquo;we&rdquo;,
              &ldquo;us&rdquo;, &ldquo;our&rdquo;) is committed to protecting your privacy. This
              Privacy Policy explains how we collect, use, disclose, and safeguard your information
              when you use the {config.brandName} platform, a product of Anchor AI Guard Pty Ltd.
            </p>
            <p className="text-slate-300 leading-relaxed">
              {config.brandName} is an autonomous {config.id} intelligence platform that processes
              data from {config.dataSources.length} connected intelligence sources. This policy
              covers both your personal data and the {config.id}-specific data processed through our
              platform.
            </p>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mt-4">
              <p className="text-amber-300 text-sm">
                {sectorDisclaimer[config.id] ||
                  'Sector-specific data handling requirements apply as detailed in this policy.'}
              </p>
            </div>
          </section>

          {/* 2. Information We Collect */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>
              2. Information We Collect
            </h2>
            <h3 className="text-lg font-semibold text-white mb-2">2.1 Account Information</h3>
            <ul className="text-slate-300 space-y-1 list-disc list-inside">
              <li>Name, email address, and organisation name</li>
              <li>Authentication credentials (hashed and salted)</li>
              <li>
                Billing information (processed via sovereign bank transfer — we never store card
                details)
              </li>
              <li>Professional role and {config.id} sector affiliation</li>
            </ul>
            <h3 className="text-lg font-semibold text-white mb-2 mt-4">2.2 Platform Usage Data</h3>
            <ul className="text-slate-300 space-y-1 list-disc list-inside">
              <li>Dashboard interactions and feature usage analytics</li>
              <li>TAZZIEDEVIL2.0 chatbot conversation logs (used to improve responses)</li>
              <li>API usage metrics and query patterns</li>
              <li>Intelligence report generation history</li>
            </ul>
            <h3 className="text-lg font-semibold text-white mb-2 mt-4">
              2.3 {config.id.charAt(0).toUpperCase() + config.id.slice(1)} Intelligence Data
            </h3>
            <p className="text-slate-300 leading-relaxed">
              {config.brandName} processes intelligence from {config.dataSources.length} connected
              sources including{' '}
              {config.dataSources
                .slice(0, 3)
                .map((ds) => ds.name)
                .join(', ')}
              . This data is processed in accordance with each source&rsquo;s data licensing terms
              and applicable {config.id} sector regulations.
            </p>
          </section>

          {/* 3. How We Use Your Information */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>
              3. How We Use Your Information
            </h2>
            <ul className="text-slate-300 space-y-2">
              <li className="flex items-start gap-2">
                <span className={config.textColor}>✓</span> Providing and maintaining the{' '}
                {config.brandName} platform and intelligence services
              </li>
              <li className="flex items-start gap-2">
                <span className={config.textColor}>✓</span> Processing your {config.id} intelligence
                queries and generating reports
              </li>
              <li className="flex items-start gap-2">
                <span className={config.textColor}>✓</span> Improving our AI models and
                self-evolving intelligence engine
              </li>
              <li className="flex items-start gap-2">
                <span className={config.textColor}>✓</span> Communicating service updates, security
                alerts, and intelligence briefings
              </li>
              <li className="flex items-start gap-2">
                <span className={config.textColor}>✓</span> Ensuring platform security and
                preventing unauthorised access
              </li>
              <li className="flex items-start gap-2">
                <span className={config.textColor}>✓</span> Complying with legal obligations and{' '}
                {config.id}-specific regulatory requirements
              </li>
            </ul>
          </section>

          {/* 4. Data Security */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>4. Data Security</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {config.brandName} is built on the Anchor AI Guard security architecture — the
              world&rsquo;s most comprehensive cybersecurity platform with 42 integrated security
              layers.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'AES-256 encryption at rest and in transit',
                'SOC 2 Type II certified infrastructure',
                'Zero-trust network architecture',
                'Continuous security monitoring (24/7/365)',
                'Regular penetration testing by certified teams',
                'Air-gapped deployment available for classified environments',
                'Multi-region data residency options',
                'Automated threat detection and response',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                  <span className="text-emerald-400 text-sm">🔒</span>
                  <span className="text-slate-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* 5. Data Retention */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>5. Data Retention</h2>
            <ul className="text-slate-300 space-y-2">
              <li>
                <strong className="text-white">Account data:</strong> Retained for the duration of
                your subscription + 30 days after termination
              </li>
              <li>
                <strong className="text-white">Intelligence queries:</strong> Retained for 12 months
                for service improvement, then anonymised
              </li>
              <li>
                <strong className="text-white">TAZZIEDEVIL2.0 conversations:</strong> Retained for
                90 days, then purged
              </li>
              <li>
                <strong className="text-white">API usage logs:</strong> Retained for 24 months for
                billing and audit purposes
              </li>
              <li>
                <strong className="text-white">Demo account data:</strong> Automatically purged
                after 14 days of inactivity
              </li>
            </ul>
          </section>

          {/* 6. Your Rights */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>6. Your Rights</h2>
            <p className="text-slate-300 leading-relaxed mb-3">
              Under the Australian Privacy Act 1988, GDPR, and applicable {config.id} sector
              regulations, you have the right to:
            </p>
            <ul className="text-slate-300 space-y-1 list-disc list-inside">
              <li>Access your personal data held by {config.brandName}</li>
              <li>Correct inaccurate or incomplete personal data</li>
              <li>Request deletion of your personal data (&ldquo;right to be forgotten&rdquo;)</li>
              <li>Object to processing of your personal data</li>
              <li>Request data portability in a machine-readable format</li>
              <li>Withdraw consent at any time</li>
              <li>
                Lodge a complaint with the Office of the Australian Information Commissioner (OAIC)
              </li>
            </ul>
          </section>

          {/* 7. Third-Party Data Sharing */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>
              7. Third-Party Data Sharing
            </h2>
            <p className="text-slate-300 leading-relaxed mb-3">We share data only with:</p>
            <ul className="text-slate-300 space-y-1 list-disc list-inside">
              <li>
                <strong className="text-white">Anchor AI Guard Pty Ltd</strong> — parent company and
                security infrastructure provider
              </li>
              <li>
                <strong className="text-white">Technology partners</strong> (
                {config.technologyPartners.map((p) => p.name).join(', ')}) — for integrated
                intelligence services under strict data processing agreements
              </li>
              <li>
                <strong className="text-white">Commonwealth Bank of Australia</strong> — sovereign
                payment processing (bank transfer)
              </li>
              <li>
                <strong className="text-white">Law enforcement</strong> — only when required by law
                or court order
              </li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-3">
              We never sell your personal data to third parties.
            </p>
          </section>

          {/* 8. Contact */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>8. Contact Us</h2>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <p className="text-white font-semibold mb-2">{config.companyStructure.entity}</p>
              <p className="text-slate-400 text-sm">Privacy Officer</p>
              <p className="text-slate-400 text-sm">📧 privacy@{config.domain}</p>
              <p className="text-slate-400 text-sm">📍 {config.companyStructure.hq}</p>
              <p className="text-slate-400 text-sm mt-3">
                Office of the Australian Information Commissioner (OAIC):{' '}
                <span className="text-cyan-400">www.oaic.gov.au</span>
              </p>
            </div>
          </section>
        </div>

        {/* Back Button */}
        <div className="mt-12 flex gap-4">
          <button
            onClick={onBack}
            className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity`}
          >
            &larr; Back to {config.brandName}
          </button>
          <button
            onClick={() => onNavigate(`anchor-${config.id}-terms`)}
            className="bg-white/5 border border-white/10 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors"
          >
            View Terms of Service
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 border-t border-white/5 pt-6">
          <p className="text-xs text-slate-600">
            &copy; {year} {config.companyStructure.entity}. All rights reserved. A product of Anchor
            AI Guard Pty Ltd.
          </p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// SECTOR TERMS OF SERVICE
// ═══════════════════════════════════════════════════════════════════════
export const SectorTermsOfService: React.FC<SectorLegalPageProps> = ({
  config,
  onBack,
  onNavigate,
}) => {
  const year = new Date().getFullYear();

  const sectorSpecificTerms: Record<string, string[]> = {
    medical: [
      'Medical Intelligence Limitation: Anchor Medical is a research support tool only. It does not diagnose, treat, or prescribe. All outputs must be reviewed by qualified healthcare professionals before clinical application.',
      'HIPAA Compliance: Subscribers handling Protected Health Information (PHI) must maintain a Business Associate Agreement (BAA) with Anchor Medical Intelligence Pty Ltd.',
      'Clinical Decision Support: Any clinical decision support features are informational only and do not replace professional medical judgement.',
    ],
    finance: [
      'Not Financial Advice: Anchor Finance provides intelligence and analytics only. Outputs do not constitute financial, investment, or trading advice. Users are responsible for their own investment decisions.',
      'Market Data Disclaimer: Market data is provided on a best-efforts basis and may be delayed. Anchor Finance is not liable for trading losses based on platform intelligence.',
      'Regulatory Compliance: Users are responsible for ensuring their use of Anchor Finance complies with applicable financial regulations in their jurisdiction (e.g., SEC, FCA, ASIC, MiFID II).',
    ],
    legal: [
      'Not Legal Advice: Anchor Legal provides intelligence and analytics only. Outputs do not constitute legal advice and should not be relied upon as a substitute for qualified legal counsel.',
      'Privilege and Confidentiality: Anchor Legal maintains strict data separation. Client-uploaded documents are encrypted and isolated. No client data is used for AI model training.',
      'Jurisdictional Scope: Legal intelligence covers 190+ jurisdictions on a best-efforts basis. Users should verify critical regulatory information with local counsel.',
    ],
    defence: [
      'Security Classification: Users must not input classified information into Anchor Defence unless deployed in an approved air-gapped environment with appropriate security clearances.',
      'Export Controls: Use of Anchor Defence may be subject to ITAR, EAR, and Australian Defence export controls. Users are responsible for compliance.',
      'Sovereign Deployment: Air-gapped sovereign deployments are governed by separate agreements and are subject to additional security requirements.',
    ],
    climate: [
      'Climate Projections: Climate risk predictions are based on current models and data. Actual outcomes may vary. Anchor Climate is not liable for losses based on climate projections.',
      'Carbon Credits: Any carbon credit marketplace features are facilitated services only. Anchor Climate does not guarantee the accuracy or validity of carbon credits offered by third parties.',
      'ESG Reporting: ESG reports generated by the platform are for informational purposes. Users should verify regulatory reporting requirements with qualified advisors.',
    ],
    energy: [
      'Grid Data: Grid stability predictions are based on available telemetry and models. Anchor Energy is not liable for grid operations decisions based on platform intelligence.',
      'Commodity Trading: Energy commodity intelligence is informational only and does not constitute trading advice.',
      'Critical Infrastructure: Users operating critical energy infrastructure must implement additional safeguards as required by NERC CIP or equivalent regulations.',
    ],
    maritime: [
      'Navigation: Anchor Maritime intelligence is supplementary to, not a replacement for, proper maritime navigation, COLREGS compliance, and bridge watchkeeping.',
      'AIS Data: AIS data may be incomplete or delayed. Vessel positions shown are best-effort approximations.',
      'Sanctions Screening: Sanctions screening results are informational. Users must conduct their own due diligence for sanctions compliance.',
    ],
    space: [
      'Orbital Data: Orbital tracking and conjunction analysis are based on publicly available TLE data and proprietary models. Anchor Space is not liable for collision events.',
      'Export Controls: Space-related intelligence may be subject to ITAR export controls. Users are responsible for compliance.',
      'Satellite Operations: Conjunction alerts and manoeuvre recommendations are advisory only. Satellite operators retain sole responsibility for space operations decisions.',
    ],
    agriculture: [
      'Crop Predictions: Crop health assessments and yield predictions are based on satellite imagery and models. Actual yields may vary significantly.',
      'Pest Warnings: Pest and disease predictions are advisory. Farmers should consult local agricultural extension services for treatment recommendations.',
      'Commodity Prices: Agricultural commodity forecasts are informational only and do not constitute trading advice.',
    ],
    education: [
      'Student Data: Anchor Education complies with FERPA, GDPR Article 8, and the Australian Privacy Act. Student data is processed solely for educational intelligence purposes.',
      'Outcome Predictions: Student outcome predictions are statistical models intended to support early intervention. They do not determine student capability or potential.',
      'Curriculum Recommendations: Curriculum alignment recommendations are advisory. Educational institutions retain sole responsibility for curriculum decisions.',
    ],
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} py-16`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={onBack}
            className="text-white/70 hover:text-white text-sm mb-6 flex items-center gap-1 transition-colors"
          >
            &larr; Back to {config.brandName}
          </button>
          <div className="flex items-center gap-3 mb-4">
            <img
              src={config.logoUrl}
              alt={config.brandName}
              draggable="false"
              className="h-12 w-12 rounded-xl object-cover select-none pointer-events-none shadow-lg"
            />
            <div>
              <h1 className="text-3xl font-bold">Terms of Service</h1>
              <p className="text-white/70 text-sm">{config.companyStructure.entity}</p>
            </div>
          </div>
          <p className="text-white/60 text-sm">
            Last updated: 1 January {year} &bull; Effective: 1 January {year}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-invert prose-sm max-w-none space-y-8">
          {/* 1. Acceptance */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>1. Acceptance of Terms</h2>
            <p className="text-slate-300 leading-relaxed">
              By accessing or using {config.brandName} (&ldquo;the Service&rdquo;), operated by{' '}
              {config.companyStructure.entity}, a subsidiary of Anchor AI Guard Pty Ltd, you agree
              to be bound by these Terms of Service. If you do not agree, do not use the Service.
            </p>
            <p className="text-slate-300 leading-relaxed mt-2">
              These Terms apply to all users, including demo account users, free trial users, paid
              subscribers, and API consumers.
            </p>
          </section>

          {/* 2. Description of Service */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>
              2. Description of Service
            </h2>
            <p className="text-slate-300 leading-relaxed">
              {config.brandName} is an autonomous {config.id} intelligence platform that provides:
            </p>
            <ul className="text-slate-300 space-y-1 list-disc list-inside mt-2">
              {config.features.slice(0, 4).map((f, i) => (
                <li key={i}>
                  {f.title} — {f.description.substring(0, 80)}...
                </li>
              ))}
              <li>
                TAZZIEDEVIL2.0 AI Assistant — intelligent chatbot for platform navigation and{' '}
                {config.id} queries
              </li>
              <li>
                Developer API access via {config.apiProducts.length} production-grade endpoints
              </li>
              <li>Cross-sector correlation intelligence via the Anchor Ecosystem</li>
            </ul>
          </section>

          {/* 3. Subscriptions & Billing */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>
              3. Subscriptions &amp; Billing
            </h2>
            <p className="text-slate-300 leading-relaxed mb-3">
              {config.brandName} offers the following subscription tiers:
            </p>
            <div className="grid gap-3">
              {(config.pricing ?? []).map((tier, i) => (
                <div
                  key={i}
                  className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <span className="font-semibold text-white">{tier.name}</span>
                    <span className="text-slate-400 text-sm ml-2">
                      {tier.features.length} features
                    </span>
                  </div>
                  <span className={`font-bold ${config.textColor}`}>
                    {tier.price}
                    {tier.period}
                  </span>
                </div>
              ))}
            </div>
            <ul className="text-slate-300 space-y-1 list-disc list-inside mt-4">
              <li>Free trial: 14 days, full access, no credit card required</li>
              <li>Billing: Monthly or annual (annual saves 20%)</li>
              <li>Cancellation: Cancel anytime; access continues until end of billing period</li>
              <li>Refunds: Pro-rata refund within first 30 days for annual plans</li>
              <li>Payment: Processed securely via sovereign bank transfer</li>
            </ul>
          </section>

          {/* 4. Demo Accounts */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>4. Demo Accounts</h2>
            <p className="text-slate-300 leading-relaxed">
              Demo accounts provide read-only access to {config.brandName} with sample intelligence
              data. Demo accounts:
            </p>
            <ul className="text-slate-300 space-y-1 list-disc list-inside mt-2">
              <li>Do not require registration or personal information</li>
              <li>Display synthetic intelligence data representative of real capabilities</li>
              <li>Cannot create, modify, or export intelligence reports</li>
              <li>Are limited to 30 minutes per session</li>
              <li>Include access to TAZZIEDEVIL2.0 chatbot in demo mode</li>
              <li>Are automatically purged — no session data is retained</li>
            </ul>
          </section>

          {/* 5. API Usage */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>5. API Terms of Use</h2>
            <p className="text-slate-300 leading-relaxed mb-3">
              {config.brandName} provides {config.apiProducts.length} production-grade API
              endpoints:
            </p>
            <div className="grid gap-2">
              {config.apiProducts.map((api, i) => (
                <div
                  key={i}
                  className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center justify-between"
                >
                  <code className="text-cyan-400 text-xs font-mono">{api.endpoint}</code>
                  <span className="text-slate-400 text-sm">{api.pricePerCall}/call</span>
                </div>
              ))}
            </div>
            <ul className="text-slate-300 space-y-1 list-disc list-inside mt-4">
              <li>API keys are non-transferable and must not be shared</li>
              <li>Rate limits apply per plan tier</li>
              <li>API responses may not be resold without a data licensing agreement</li>
              <li>We reserve the right to throttle or suspend API access for abuse</li>
            </ul>
          </section>

          {/* 6. Acceptable Use */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>6. Acceptable Use</h2>
            <p className="text-slate-300 leading-relaxed mb-2">You agree not to:</p>
            <ul className="text-slate-300 space-y-1 list-disc list-inside">
              <li>Use {config.brandName} for any unlawful purpose</li>
              <li>Attempt to reverse-engineer, decompile, or extract the AI models</li>
              <li>Use automated tools to scrape or mass-download intelligence data</li>
              <li>Share login credentials or API keys with unauthorised third parties</li>
              <li>Upload malicious content or attempt to compromise platform security</li>
              <li>
                Misrepresent {config.brandName} intelligence outputs as your own original research
                without attribution
              </li>
              <li>
                Use the platform to harm individuals, organisations, or critical infrastructure
              </li>
            </ul>
          </section>

          {/* 7. Intellectual Property */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>
              7. Intellectual Property
            </h2>
            <p className="text-slate-300 leading-relaxed">
              {config.brandName}, the {config.brandName} logo, TAZZIEDEVIL2.0, and all associated AI
              models, algorithms, and intelligence outputs are the intellectual property of{' '}
              {config.companyStructure.entity} and Anchor AI Guard Pty Ltd.
            </p>
            <p className="text-slate-300 leading-relaxed mt-2">
              Subscribers receive a non-exclusive, non-transferable licence to use intelligence
              outputs for their internal business purposes during their active subscription.
            </p>
          </section>

          {/* 8. Sector-Specific Terms */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>
              8. {config.id.charAt(0).toUpperCase() + config.id.slice(1)}-Specific Terms
            </h2>
            <div className="space-y-3">
              {(sectorSpecificTerms[config.id] || []).map((term, i) => (
                <div key={i} className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-4">
                  <p className="text-slate-300 text-sm leading-relaxed">{term}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 9. Limitation of Liability */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>
              9. Limitation of Liability
            </h2>
            <p className="text-slate-300 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, {config.companyStructure.entity.toUpperCase()}{' '}
              AND ANCHOR AI GUARD PTY LTD SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF{' '}
              {config.brandName.toUpperCase()}.
            </p>
            <p className="text-slate-300 leading-relaxed mt-2">
              Our total aggregate liability shall not exceed the fees paid by you in the 12 months
              preceding the claim.
            </p>
          </section>

          {/* 10. Governing Law */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>10. Governing Law</h2>
            <p className="text-slate-300 leading-relaxed">
              These Terms are governed by the laws of the State of Victoria, Australia. Any disputes
              shall be resolved in the courts of Victoria, Australia, unless otherwise required by
              applicable {config.id} sector regulations or international agreements.
            </p>
          </section>

          {/* 11. Contact */}
          <section>
            <h2 className={`text-xl font-bold ${config.textColor} mb-3`}>11. Contact Us</h2>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <p className="text-white font-semibold mb-2">{config.companyStructure.entity}</p>
              <p className="text-slate-400 text-sm">Legal Department</p>
              <p className="text-slate-400 text-sm">📧 legal@{config.domain}</p>
              <p className="text-slate-400 text-sm">📍 {config.companyStructure.hq}</p>
            </div>
          </section>
        </div>

        {/* Back Button */}
        <div className="mt-12 flex gap-4">
          <button
            onClick={onBack}
            className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity`}
          >
            &larr; Back to {config.brandName}
          </button>
          <button
            onClick={() => onNavigate(`anchor-${config.id}-privacy`)}
            className="bg-white/5 border border-white/10 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors"
          >
            View Privacy Policy
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 border-t border-white/5 pt-6">
          <p className="text-xs text-slate-600">
            &copy; {year} {config.companyStructure.entity}. All rights reserved. A product of Anchor
            AI Guard Pty Ltd.
          </p>
        </div>
      </div>
    </div>
  );
};
